import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Send, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../../services/api';
import toast from 'react-hot-toast';
import useAuthStore from '../../context/authStore';
import { StaffingRequestModal } from '../../components/StaffingRequestModal';

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

function getRequestPositions(request) {
  if (Array.isArray(request?.positions) && request.positions.length > 0) {
    return request.positions;
  }
  if (!request?.position) return [];
  return [{
    key: 'pos-1',
    position: request.position,
    department: request.department || '',
    numberOfWorkersNeeded: Math.max(1, Number(request.numberOfWorkersNeeded || 1)),
  }];
}

function getPositionSummary(request) {
  const positions = getRequestPositions(request);
  if (!positions.length) return 'No positions';
  if (positions.length === 1) {
    return positions[0].department ? `${positions[0].position} · ${positions[0].department}` : positions[0].position;
  }
  const first = positions[0];
  const firstLabel = first.department ? `${first.position} · ${first.department}` : first.position;
  return `${firstLabel} +${positions.length - 1} more`;
}

function getTotalWorkersNeeded(request) {
  return getRequestPositions(request).reduce((sum, row) => sum + Math.max(1, Number(row.numberOfWorkersNeeded || 1)), 0);
}

function getPositionRejectionSummary(request) {
  return getRequestPositions(request)
    .map((row) => {
      const count = Math.max(0, Number(row?.matchRejectionCount || 0));
      if (!count) return null;
      const label = row?.department ? `${row.position} · ${row.department}` : row?.position || 'Position';
      return `${label}: ${count}`;
    })
    .filter(Boolean);
}

export default function StaffingRequestsPage() {
  const { user } = useAuthStore();
  const [organizationId, setOrganizationId] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [pendingRateApprovals, setPendingRateApprovals] = useState(0);
  const [unconfiguredFacilities, setUnconfiguredFacilities] = useState([]);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const orgResponse = await apiGet('/organizations/me');
      const org = orgResponse.organization || orgResponse.org;

      setOrganizationId(org?._id);

      if (!org?.services?.staffing?.enabled && !org?.staffingService?.enabled) {
        toast.error('Staffing service is not enabled for your organization');
        setLoading(false);
        return;
      }

      const data = await apiGet('/staffing-requests/my-requests');
      setRequests(data.requests || []);

      if (user?.role === 'facility_admin' && org?._id) {
        const approvalData = await apiGet(`/rate-tiers/${org._id}/rate-tiers-approval`);
        const pending = approvalData?.summary?.pending_client_review ?? 0;
        setPendingRateApprovals(pending);
      } else {
        setPendingRateApprovals(0);
      }

      // Check for facilities with unconfigured staffing preferences
      try {
        const facilitiesRes = await apiGet('/organizations/me/facilities');
        const facilities = facilitiesRes?.facilities || [];
        const unconfigured = facilities.filter(
          (f) => f.requirements?.minExperience == null
        );
        setUnconfiguredFacilities(unconfigured);
      } catch {
        // non-blocking
      }
    } catch (error) {
      toast.error('Failed to load staffing requests');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingRequest(null);
  };

  const handleNewRequest = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEditRequest = (request) => {
    setEditingRequest(request);
    setShowModal(true);
  };

  const handleSave = () => {
    fetchRequests();
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (requestId) => {
    try {
      await apiPost(`/staffing-requests/${requestId}/submit`, {});
      toast.success('Request submitted for approval');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to submit request');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Delete this draft request? This cannot be undone.')) return;
    try {
      const { apiDelete } = await import('../../services/api');
      await apiDelete(`/staffing-requests/${requestId}`);
      toast.success('Request deleted');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staffing Requests</h1>
          <p className="text-gray-600 mt-1">Request staffing resources for your organization</p>
        </div>
        <button
          onClick={handleNewRequest}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          New Request
        </button>
      </div>

      {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How it works:</p>
            <p>1. Create a staffing request in draft mode<br/>2. Fill in details and submit<br/>3. We review and approve<br/>4. We propose workers and send an SPO for signature</p>
          </div>
        </div>

      {pendingRateApprovals > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start justify-between gap-4">
          <div className="flex gap-3">
            <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Action required before new submissions</p>
              <p>
                You have {pendingRateApprovals} rate tier{pendingRateApprovals > 1 ? 's' : ''} awaiting approval.
                New staffing requests require approved rates.
              </p>
            </div>
          </div>
          <Link to="/staffing/rate-approvals" className="px-3 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 whitespace-nowrap">
            Review Rates
          </Link>
        </div>
      )}

      {unconfiguredFacilities.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start justify-between gap-4">
          <div className="flex gap-3">
            <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Staffing preferences not yet configured</p>
              <p>
                {unconfiguredFacilities.length === 1
                  ? `${unconfiguredFacilities[0].name} hasn't set up staffing preferences yet.`
                  : `${unconfiguredFacilities.length} facilities haven't configured staffing preferences yet.`}
                {' '}Set up minimum experience, credential requirements and orientation preferences so Stratel can match the right workers.
              </p>
            </div>
          </div>
          <Link to="/settings?tab=organization&orgTab=organization-settings" className="px-3 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 whitespace-nowrap">
            Configure
          </Link>
        </div>
      )}

      {/* Requests List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center p-8 text-gray-500">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Clock size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">No staffing requests yet</p>
            <p className="text-sm text-gray-500 mt-1">Create your first staffing request to get started</p>
          </div>
        ) : (
          requests.map((request) => (
            <div key={request._id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{getPositionSummary(request)}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[request.status]}`}>
                      {request.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    {[request.facilityId?.name, request.location?.facility].filter(Boolean).join(' · ') || 'Location TBD'}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">Start Date</p>
                      <p className="font-medium text-gray-900">{new Date(request.startDate).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Workers Needed</p>
                      <p className="font-medium text-gray-900">{getTotalWorkersNeeded(request)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Wage</p>
                      <p className="font-medium text-gray-900">${request.wage || 'TBD'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Urgency</p>
                      <p className="font-medium text-gray-900 capitalize">{request.urgency}</p>
                    </div>
                  </div>
                  {request.assignedWorkers?.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm font-medium text-gray-600 mb-2">Assigned Workers:</p>
                      {getPositionRejectionSummary(request).length > 0 && (
                        <div className="mb-2 space-y-1">
                          <p className="text-xs text-amber-700">
                            Match rejections used for requested positions:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {getPositionRejectionSummary(request).map((summary) => (
                              <span
                                key={summary}
                                className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700"
                              >
                                {summary}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {request.assignedWorkers.map((aw) => {
                          const workerId = aw.workerId?._id || aw.workerId;
                          const workerName =
                            `${aw.workerId?.firstName || ''} ${aw.workerId?.lastName || ''}`.trim() || 'Worker';
                          const status = String(aw.status || 'proposed').toLowerCase();
                          const isProposed = status === 'proposed';
                          return (
                            <div key={workerId} className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-gray-900">{workerName}</span>
                                <span className="text-gray-600">${aw.hourlyRate}/hr</span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide ${
                                  status === 'assigned' || status === 'accepted'
                                    ? 'bg-green-100 text-green-700'
                                    : status === 'rejected'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {status}
                                </span>
                                 {isProposed && ['facility_admin', 'facility_manager'].includes(user?.role) && (
                                   <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
                                     Review the SPO when sent
                                   </span>
                                 )}
                              </div>
                              {status === 'rejected' && aw.rejectionReason && (
                                <p className="text-red-700 mt-1">Reason: {aw.rejectionReason}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {request.status === 'draft' && (
                    <>
                      <button
                        onClick={() => handleEditRequest(request)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleSubmit(request._id)}
                        className="px-3 py-2 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 flex items-center gap-1"
                      >
                        <Send size={16} />
                        Submit
                      </button>
                    </>
                  )}
                  {request.status === 'draft' && (
                    <button
                      onClick={() => handleDelete(request._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      <StaffingRequestModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        onSave={handleSave}
        editingRequest={editingRequest}
        organizationId={organizationId}
      />
    </div>
  );
}

